const SNAPSHOT_VERSION = 2;
const DEMO_BUSINESS_UNIT_ID = 900001;
const PRODUCTION_BUSINESS_UNIT_ID = 14;
const COLLECTIONS = [
  "roomCategories", "rooms", "reservations", "customers", "clients",
  "roomRates", "cityEvents", "roomBlocks",
];

let snapshot = null;
let snapshotPromise = null;

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validDateOnly(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(`${value}T12:00:00Z`))
    && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
}

function invalidSnapshot() {
  const error = new Error("The Hotel Travesía demo snapshot is unavailable or invalid.");
  error.code = "PILCORE_DEMO_SNAPSHOT_INVALID";
  return error;
}

const RATE_SUMMARY_FIELDS = [
  "id", "business_unit_id", "room_category_id", "client_id", "name", "rate_code", "currency", "status",
  "adults", "children", "max_occupancy", "adults_match_type", "children_match_type", "max_occupancy_match_type",
  "is_refundable", "show_in_booking_engine", "group_key", "penalty_type", "base_rate_id", "adjustment_type",
  "adjustment_value", "min_price_amount", "max_price_amount", "default_min_stay", "default_max_stay",
  "valid_from", "valid_to", "room_category", "sister_categories", "client", "base_rate", "has_formula_link",
];

function hasRequiredFields(row, required) {
  return isObject(row) && required.every((key) => Object.prototype.hasOwnProperty.call(row, key));
}

function nonEmptyString(value) {
  return typeof value === "string" && Boolean(value.trim());
}

function validRateInstant(value) {
  return typeof value === "string"
    && /^\d{4}-\d{2}-\d{2}T/.test(value) && validDateOnly(value.slice(0, 10)) && Number.isFinite(Date.parse(value));
}

function validRateDecimal(value) {
  return value === null || ((typeof value === "number" || typeof value === "string")
    && /^-?\d+(?:\.\d+)?$/.test(String(value)) && Number.isFinite(Number(value)));
}

function validateRoomRateConfigurations(value, collectionName) {
  if (!Array.isArray(value[collectionName])) throw invalidSnapshot();
  const rates = new Map(value.roomRates.map((row) => [row.id, row]));
  const categories = new Map(value.roomCategories.map((row) => [row.id, row]));
  const clients = new Map(value.clients.map((row) => [row.id, row]));
  const seen = new Set();
  const validId = (id) => Number.isSafeInteger(id) && id > 0;
  const validCategory = (row) => hasRequiredFields(row, ["id", "name", "business_unit_id"])
    && validId(row.id) && row.business_unit_id === DEMO_BUSINESS_UNIT_ID
    && categories.get(row.id)?.business_unit_id === DEMO_BUSINESS_UNIT_ID
    && nonEmptyString(row.name) && categories.get(row.id)?.name === row.name;
  const validBaseRate = (row, id) => id === null ? row === null
    : hasRequiredFields(row, ["id", "name", "rate_code"]) && validId(id) && row.id === id
      && nonEmptyString(row.name) && nonEmptyString(row.rate_code)
      && rates.get(id)?.business_unit_id === DEMO_BUSINESS_UNIT_ID
      && rates.get(id)?.name === row.name && rates.get(id)?.rate_code === row.rate_code;
  for (const row of value[collectionName]) {
    if (!hasRequiredFields(row, RATE_SUMMARY_FIELDS)
      || !validId(row.id) || seen.has(row.id) || !rates.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || rates.get(row.id)?.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || !["room_category_id", "client_id", "name", "rate_code"].every((key) => rates.get(row.id)?.[key] === row[key])
      || !validCategory(row.room_category) || row.room_category.id !== row.room_category_id
      || !Array.isArray(row.sister_categories) || !row.sister_categories.length
      || !row.sister_categories.every(validCategory)
      || !(collectionName === "roomRateSummaries"
        ? row.sister_categories[0].id === row.room_category_id
        : row.sister_categories.some((category) => category.id === row.room_category_id))
      || !["name", "rate_code", "currency"].every((key) => nonEmptyString(row[key]))
      || !["active", "inactive"].includes(row.status)
      || !["adults", "children", "max_occupancy", "default_min_stay", "default_max_stay"]
        .every((key) => row[key] === null || (Number.isSafeInteger(row[key]) && row[key] >= 0))
      || !["adults_match_type", "children_match_type", "max_occupancy_match_type"]
        .every((key) => [null, "exact", "upto"].includes(row[key]))
      || typeof row.is_refundable !== "boolean" || typeof row.show_in_booking_engine !== "boolean"
      || typeof row.has_formula_link !== "boolean"
      || !(row.group_key === null || typeof row.group_key === "string")
      || ![null, "percent", "fixed"].includes(row.penalty_type)
      || ![null, "percent", "absolute"].includes(row.adjustment_type)
      || !["adjustment_value", "min_price_amount", "max_price_amount"].every((key) => validRateDecimal(row[key]))
      || !validRateInstant(row.valid_from) || !(row.valid_to === null || validRateInstant(row.valid_to))
      || !validBaseRate(row.base_rate, row.base_rate_id)) throw invalidSnapshot();
    if (collectionName === "roomRateDetails" && Object.prototype.hasOwnProperty.call(row, "matrix_has_formula_link")
      && typeof row.matrix_has_formula_link !== "boolean") throw invalidSnapshot();
    if (row.client_id === null ? row.client !== null
      : !hasRequiredFields(row.client, ["id", "name"]) || !validId(row.client_id)
        || row.client.id !== row.client_id || !nonEmptyString(row.client.name)
        || clients.get(row.client_id)?.name !== row.client.name) throw invalidSnapshot();
    if (row.has_formula_link) {
      if (!["average", "min", "max", "weighted", "custom"].includes(row.formula_type)
        || !Array.isArray(row.formula_inputs)
        || !row.formula_inputs.every((input) => hasRequiredFields(input, ["base_rate_id", "base_rate_name", "rate_code"])
          && validId(input.base_rate_id) && nonEmptyString(input.base_rate_name) && nonEmptyString(input.rate_code)
          && rates.get(input.base_rate_id)?.business_unit_id === DEMO_BUSINESS_UNIT_ID
          && rates.get(input.base_rate_id)?.name === input.base_rate_name
          && rates.get(input.base_rate_id)?.rate_code === input.rate_code)) throw invalidSnapshot();
    } else if (Object.prototype.hasOwnProperty.call(row, "formula_type")
      || Object.prototype.hasOwnProperty.call(row, "formula_inputs")) throw invalidSnapshot();
    seen.add(row.id);
  }
  if (collectionName === "roomRateDetails" && (seen.size !== rates.size
    || [...rates.keys()].some((id) => !seen.has(id)))) throw invalidSnapshot();
  if (collectionName === "roomRateDetails") {
    const groupCategories = new Map();
    const groupKey = (row) => row.group_key ? `group:${row.group_key}` : `single:${row.id}`;
    for (const row of [...value.roomRateDetails].sort((left, right) => left.id - right.id)) {
      const key = groupKey(row);
      const categoryIds = groupCategories.get(key) || [];
      categoryIds.push(row.room_category_id);
      groupCategories.set(key, categoryIds);
    }
    for (const row of value.roomRateDetails) {
      const categoryIds = groupCategories.get(groupKey(row));
      if (row.sister_categories.length !== categoryIds.length
        || categoryIds.some((id, index) => row.sister_categories[index].id !== id)) throw invalidSnapshot();
    }
  }
}

function validateRateCalendar(value, collectionName) {
  if (!Array.isArray(value[collectionName])) throw invalidSnapshot();
  const rates = new Map(value.roomRates.map((row) => [row.id, row]));
  const seen = new Set();
  const isPrices = collectionName === "ratePrices";
  const fields = ["id", "room_rate_id", "business_unit_id", ...(isPrices
    ? ["price", "start_date", "end_date", "min_stay", "max_stay"] : ["stay_date"])];
  for (const row of value[collectionName]) {
    if (!hasRequiredFields(row, fields) || !Number.isSafeInteger(row.id) || row.id <= 0 || seen.has(row.id)
      || !Number.isSafeInteger(row.room_rate_id) || row.room_rate_id <= 0
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || rates.get(row.room_rate_id)?.business_unit_id !== DEMO_BUSINESS_UNIT_ID) throw invalidSnapshot();
    if (isPrices) {
      if (row.price === null || !validRateDecimal(row.price) || Number(row.price) < 0
        || !validDateOnly(row.start_date) || !validDateOnly(row.end_date) || row.start_date > row.end_date
        || !["min_stay", "max_stay"].every((key) => row[key] === null
          || (Number.isSafeInteger(row[key]) && row[key] >= 0))) throw invalidSnapshot();
      if (Object.prototype.hasOwnProperty.call(row, "stay_date") && !validDateOnly(row.stay_date)) throw invalidSnapshot();
    } else if (!validDateOnly(row.stay_date)
      || (Object.prototype.hasOwnProperty.call(row, "is_automatic") && row.is_automatic !== null
        && typeof row.is_automatic !== "boolean")) throw invalidSnapshot();
    seen.add(row.id);
  }
}

const RESERVATION_SETTINGS_FIELDS = [
  "business_unit_id", "max_age_baby", "max_age_child", "min_age_main_guest", "min_age_booker",
  "require_company_booker_contact", "vip_categories", "checkin_time", "checkout_time",
  "clients_require_requested_flow", "group_reservations_default_status", "required_customer_fields_reservation",
  "required_customer_roles_reservation", "reservation_manual_modification_policy",
];
const RESERVATION_SETTINGS_ROLES = ["booker", "main_guest", "accompanying", "child"];
const RESERVATION_SETTINGS_CUSTOMER_FIELDS = [
  "salutation", "first_name", "middle_name", "last_name", "second_last_name", "gender", "birth_date", "email", "phone",
  "country", "city", "address", "postal_code", "document_type", "document_number", "is_vip", "vip_level", "photo_url", "customer_description",
];
const RESERVATION_POLICY_SOURCES = ["pms", "booking_engine", "channel_manager"];
const RESERVATION_POLICY_ACTIONS = [
  "allow_modifications", "allow_cancellation", "allow_date_changes", "allow_guest_changes", "allow_room_category_changes", "allow_rate_price_changes",
];

function onlyFields(row, allowed) {
  return isObject(row) && Object.keys(row).every((key) => allowed.includes(key));
}

// Raw persisted configuration is kept separate from the smaller hotelSettings
// used by other demo views. Nullable values and partial policies are legitimate
// PMS settings; missing columns are not interchangeable with explicit nulls.
export function validateDemoReservationSettings(settings, clients) {
  if (!hasRequiredFields(settings, RESERVATION_SETTINGS_FIELDS) || !onlyFields(settings, RESERVATION_SETTINGS_FIELDS)
    || settings.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !["max_age_baby", "max_age_child", "min_age_main_guest", "min_age_booker"].every((key) => settings[key] === null
      || (Number.isSafeInteger(settings[key]) && settings[key] >= 0))
    || typeof settings.require_company_booker_contact !== "boolean"
    || !(settings.vip_categories === null || typeof settings.vip_categories === "string")
    || !["checkin_time", "checkout_time"].every((key) => settings[key] === null || (typeof settings[key] === "string"
      && /^(?:(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,6})?)?|24:00(?::00(?:\.0{1,6})?)?)$/.test(settings[key])))
    || ![null, "Requested", "Confirmed"].includes(settings.group_reservations_default_status)) throw invalidSnapshot();

  if (!Array.isArray(clients)) throw invalidSnapshot();
  const clientIds = new Set();
  for (const client of clients) {
    if (!isObject(client) || !Number.isSafeInteger(client.id) || client.id <= 0 || clientIds.has(client.id)
      || !nonEmptyString(client.name) || !["active", "inactive"].includes(client.status)) throw invalidSnapshot();
    clientIds.add(client.id);
  }
  const flow = settings.clients_require_requested_flow;
  if (!(flow === null || typeof flow === "string")) throw invalidSnapshot();
  if (typeof flow === "string" && flow.trim()) {
    for (const raw of flow.split(",")) {
      const token = raw.trim();
      if (!/^(?:null|[1-9]\d*)$/.test(token)
        || (token !== "null" && (!Number.isSafeInteger(Number(token)) || !clientIds.has(Number(token))))) throw invalidSnapshot();
    }
  }

  const fields = settings.required_customer_fields_reservation;
  if (fields !== null && (!onlyFields(fields, RESERVATION_SETTINGS_ROLES)
    || Object.values(fields).some((values) => !Array.isArray(values)
      || values.some((key) => !RESERVATION_SETTINGS_CUSTOMER_FIELDS.includes(key))))) throw invalidSnapshot();
  const roles = settings.required_customer_roles_reservation;
  if (roles !== null && (!Array.isArray(roles) || roles.some((key) => !RESERVATION_SETTINGS_ROLES.includes(key)))) throw invalidSnapshot();
  const policy = settings.reservation_manual_modification_policy;
  if (policy !== null && (!onlyFields(policy, RESERVATION_POLICY_SOURCES)
    || Object.values(policy).some((source) => source !== null && (!onlyFields(source, RESERVATION_POLICY_ACTIONS)
      || Object.values(source).some((flag) => typeof flag !== "boolean"))))) throw invalidSnapshot();
  return settings;
}

const FRONT_DESK_SETTINGS_FIELDS = [
  "timezone", "required_customer_fields_check_in", "required_customer_roles_check_in", "require_full_payment_for_check_in",
];

// These settings already exist in the public hotelSettings catalog. Validate
// the complete persisted values and return only the Front Desk projection;
// never replace missing data with UI defaults or expose unrelated settings.
export function validateDemoFrontDeskSettings(settings) {
  if (!hasRequiredFields(settings, ["business_unit_id", ...FRONT_DESK_SETTINGS_FIELDS])
    || settings.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !nonEmptyString(settings.timezone)
    || !onlyFields(settings.required_customer_fields_check_in, RESERVATION_SETTINGS_ROLES)
    || Object.values(settings.required_customer_fields_check_in).some((fields) => !Array.isArray(fields)
      || fields.some((key) => !RESERVATION_SETTINGS_CUSTOMER_FIELDS.includes(key)))
    || !Array.isArray(settings.required_customer_roles_check_in)
    || settings.required_customer_roles_check_in.some((key) => !RESERVATION_SETTINGS_ROLES.includes(key))
    || typeof settings.require_full_payment_for_check_in !== "boolean") throw invalidSnapshot();
  try {
    new Intl.DateTimeFormat("en", { timeZone: settings.timezone }).format();
  } catch (_error) {
    throw invalidSnapshot();
  }
  return Object.fromEntries(["business_unit_id", ...FRONT_DESK_SETTINGS_FIELDS].map((key) => [key, settings[key]]));
}

const EXTRAS_CATALOG_FIELDS = [
  "id", "business_unit_id", "name", "description", "currency", "charge_period", "charge_unit", "scope",
  "min_nights", "max_nights", "min_pax", "max_pax", "amount", "amount_type", "inclusive", "apply_automatically",
  "is_mandatory", "booking_engine_visible", "room_category_ids", "room_category_targeting_mode", "room_rate_ids",
  "room_rate_targeting_mode", "valid_from", "valid_to", "is_active",
];

// Extras and targeting labels must be available as one complete read-only
// response. The existing ungrouped rate catalog includes inactive targets.
export function validateDemoExtrasSettings(value) {
  if (!isObject(value) || !Array.isArray(value.extrasCatalog) || !Array.isArray(value.roomCategories)
    || !Array.isArray(value.roomRates)) throw invalidSnapshot();
  const validId = (id) => Number.isSafeInteger(id) && id > 0;
  const categories = new Map();
  for (const row of value.roomCategories) {
    if (!isObject(row) || !validId(row.id) || categories.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || !nonEmptyString(row.name)) throw invalidSnapshot();
    categories.set(row.id, { id: row.id, name: row.name, business_unit_id: row.business_unit_id });
  }
  const rates = new Map();
  for (const row of value.roomRates) {
    if (!isObject(row) || !validId(row.id) || rates.has(row.id) || !validId(row.room_category_id)
      || !categories.has(row.room_category_id) || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || !nonEmptyString(row.name) || !nonEmptyString(row.rate_code) || !["active", "inactive"].includes(row.status)) throw invalidSnapshot();
    rates.set(row.id, { id: row.id, name: row.name, rate_code: row.rate_code, room_category_id: row.room_category_id,
      business_unit_id: row.business_unit_id, status: row.status, room_category: categories.get(row.room_category_id) });
  }
  const seen = new Set();
  for (const row of value.extrasCatalog) {
    if (!hasRequiredFields(row, EXTRAS_CATALOG_FIELDS) || !onlyFields(row, EXTRAS_CATALOG_FIELDS)
      || !validId(row.id) || seen.has(row.id) || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || !nonEmptyString(row.name) || !(row.description === null || typeof row.description === "string")
      || !(row.currency === null || (typeof row.currency === "string" && /^[a-z]{3}$/i.test(row.currency)))
      || !["per_stay", "per_night"].includes(row.charge_period)
      || !["per_room", "per_adult", "per_child", "per_pax"].includes(row.charge_unit)
      || !["per_room", "per_reservation"].includes(row.scope)
      || !["min_nights", "max_nights", "min_pax", "max_pax"].every((key) => row[key] === null || Number.isSafeInteger(row[key]))
      || row.amount === null || !validRateDecimal(row.amount)
      || !["absolute", "relative"].includes(row.amount_type)
      || !["inclusive", "apply_automatically", "is_mandatory"].every((key) => row[key] === null || typeof row[key] === "boolean")
      || typeof row.booking_engine_visible !== "boolean" || typeof row.is_active !== "boolean"
      || !["room_category_targeting_mode", "room_rate_targeting_mode"].every((key) => ["include", "exclude"].includes(row[key]))
      || !["valid_from", "valid_to"].every((key) => row[key] === null || validDateOnly(row[key]))) throw invalidSnapshot();
    for (const [key, references] of [["room_category_ids", categories], ["room_rate_ids", rates]]) {
      if (row[key] !== null && (!Array.isArray(row[key]) || row[key].some((id) => !validId(id) || !references.has(id)))) throw invalidSnapshot();
    }
    seen.add(row.id);
  }
  return { extras: value.extrasCatalog, roomCategories: [...categories.values()], roomRates: [...rates.values()] };
}

const CHARGE_TYPE_FIELDS = [
  "id", "business_unit_id", "code", "name", "description", "category", "calculation_mode", "default_amount",
  "currency", "is_active", "is_system", "is_protected", "sort_order", "upgrade_category_order",
];
const UPGRADE_RULE_FIELDS = ["id", "business_unit_id", "from_room_category_id", "to_room_category_id", "calculation_mode", "value"];
const CHARGES_SETTINGS_FIELDS = ["business_unit_id", "currency", "category_order"];
const CHARGES_COLLECTIONS = ["reservationChargeTypes", "reservationUpgradeChargeRules", "reservationChargesSettings"];

function validateChargesCollections(value, requireComplete) {
  if (!isObject(value) || !Array.isArray(value.roomCategories)) throw invalidSnapshot();
  const present = (key) => Object.prototype.hasOwnProperty.call(value, key);
  if (requireComplete && !CHARGES_COLLECTIONS.every(present)) throw invalidSnapshot();
  const validId = (id) => Number.isSafeInteger(id) && id > 0;
  const categories = new Map();
  for (const row of value.roomCategories) {
    if (!isObject(row) || !validId(row.id) || categories.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || !nonEmptyString(row.name)) throw invalidSnapshot();
    categories.set(row.id, { id: row.id, name: row.name });
  }
  const validCategoryIds = (ids) => Array.isArray(ids) && ids.every((id) => validId(id) && categories.has(id));
  if (present("reservationChargeTypes")) {
    if (!Array.isArray(value.reservationChargeTypes)) throw invalidSnapshot();
    const ids = new Set(); const codes = new Set();
    for (const row of value.reservationChargeTypes) {
      if (!hasRequiredFields(row, CHARGE_TYPE_FIELDS) || !onlyFields(row, CHARGE_TYPE_FIELDS)
        || !validId(row.id) || ids.has(row.id) || codes.has(row.code) || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
        || !["code", "name", "category", "calculation_mode"].every((key) => nonEmptyString(row[key]))
        || !(row.description === null || typeof row.description === "string")
        || !validRateDecimal(row.default_amount)
        || typeof row.currency !== "string" || !/^[a-z]{3}$/i.test(row.currency)
        || !["is_active", "is_system", "is_protected"].every((key) => typeof row[key] === "boolean")
        || !Number.isSafeInteger(row.sort_order)
        || !(row.upgrade_category_order === null || (row.code === "room_upgrade" && validCategoryIds(row.upgrade_category_order)))) throw invalidSnapshot();
      ids.add(row.id); codes.add(row.code);
    }
  }
  if (present("reservationUpgradeChargeRules")) {
    if (!Array.isArray(value.reservationUpgradeChargeRules)) throw invalidSnapshot();
    const ids = new Set(); const routes = new Set();
    for (const row of value.reservationUpgradeChargeRules) {
      if (!hasRequiredFields(row, UPGRADE_RULE_FIELDS) || !onlyFields(row, UPGRADE_RULE_FIELDS)
        || !validId(row.id) || ids.has(row.id) || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
        || !validCategoryIds([row.from_room_category_id, row.to_room_category_id])
        || row.from_room_category_id === row.to_room_category_id
        || routes.has(`${row.from_room_category_id}:${row.to_room_category_id}`)
        || !["percentage", "fixed_amount"].includes(row.calculation_mode)
        || row.value === null || !validRateDecimal(row.value) || Number(row.value) <= 0) throw invalidSnapshot();
      ids.add(row.id); routes.add(`${row.from_room_category_id}:${row.to_room_category_id}`);
    }
  }
  if (present("reservationChargesSettings")) {
    const settings = value.reservationChargesSettings;
    if (!hasRequiredFields(settings, CHARGES_SETTINGS_FIELDS) || !onlyFields(settings, CHARGES_SETTINGS_FIELDS)
      || settings.business_unit_id !== DEMO_BUSINESS_UNIT_ID
      || typeof settings.currency !== "string" || !/^[A-Z]{3}$/.test(settings.currency)
      || !validCategoryIds(settings.category_order) || settings.category_order.length !== categories.size
      || new Set(settings.category_order).size !== categories.size) throw invalidSnapshot();
  }
  return categories;
}

// The real type/upgrade GETs may seed defaults. The demo only serves the
// persisted public export; it never calls those endpoints or reads metadata.
export function validateDemoChargesSettings(value) {
  const categories = validateChargesCollections(value, true);
  const chargeType = value.reservationChargeTypes.find((row) => row.code === "room_upgrade");
  if (!chargeType) return { chargeTypes: value.reservationChargeTypes, upgradeContext: null };
  // Match the PMS category ordering: deduplicate the configured partial order
  // and append missing categories in the database's exported name/id order.
  const categoryOrder = [...new Set([...(chargeType.upgrade_category_order || []), ...value.reservationChargesSettings.category_order])];
  return {
    chargeTypes: value.reservationChargeTypes,
    upgradeContext: { charge_type: chargeType, currency: value.reservationChargesSettings.currency,
      categories: categoryOrder.map((id) => categories.get(id)), category_order: categoryOrder,
      rules: value.reservationUpgradeChargeRules },
  };
}

const BOOKING_ENGINE_SETTINGS_FIELDS = [
  "business_unit_id", "booking_engine_enabled", "booking_engine_slug", "booking_engine_entry_mode", "booking_engine_theme",
  "booking_engine_hero_background_screens", "booking_engine_languages", "booking_engine_default_language", "booking_engine_texts",
  "booking_engine_terms", "booking_engine_guest_email_templates", "booking_engine_price_includes_vat",
  "booking_engine_price_includes_other_taxes", "booking_engine_price_includes_fees", "booking_engine_price_includes_city_tax",
];
const BOOKING_ENGINE_PROFILE_FIELDS = ["id", "business_unit_id", "public_hotel_name", "address_line", "city", "postal_code", "country", "phones", "emails"];
const BOOKING_ENGINE_ROOM_COPY_FIELDS = ["id", "business_unit_id", "room_category_id", "name_en", "name_es",
  "short_description_en", "short_description_es", "size_m2", "beds_count", "max_pax", "bed_type_en", "bed_type_es", "view_en", "view_es", "highlights_en", "highlights_es"];
const BOOKING_ENGINE_AMENITY_FIELDS = ["id", "business_unit_id", "code", "name", "name_en", "name_es", "category", "category_en", "category_es", "sort_order"];
const BOOKING_ENGINE_ASSET_FIELDS = ["id", "business_unit_id", "kind", "is_active", "sort_order", "room_category_id"];
const BOOKING_ENGINE_AGGREGATE_FIELDS = ["business_unit_id", "settings", "profile", "roomCategoryCopy", "amenities", "roomCategoryAmenities", "assets"];

// A single allowlisted aggregate supplies this studio. Assets describe only
// existence, kind and category; no media URL, storage path or credentials can
// enter the response, and no configuration is fabricated for missing exports.
export function validateDemoBookingEngineSettings(value) {
  const booking = value?.bookingEngineSettings;
  const exactFields = (row, fields) => hasRequiredFields(row, fields) && onlyFields(row, fields);
  const validId = (id) => Number.isSafeInteger(id) && id > 0;
  const nullableText = (text) => text === null || typeof text === "string";
  const translatedText = (text) => onlyFields(text, ["en", "es"]) && Object.values(text).every((entry) => typeof entry === "string");
  if (!exactFields(booking, BOOKING_ENGINE_AGGREGATE_FIELDS) || booking.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !Array.isArray(value.roomCategories)) throw invalidSnapshot();
  const categories = new Map();
  for (const row of value.roomCategories) {
    if (!isObject(row) || !validId(row.id) || categories.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || !nonEmptyString(row.name)) throw invalidSnapshot();
    categories.set(row.id, { id: row.id, name: row.name, business_unit_id: row.business_unit_id });
  }
  const settings = booking.settings;
  if (!exactFields(settings, BOOKING_ENGINE_SETTINGS_FIELDS) || settings.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !["booking_engine_enabled", "booking_engine_price_includes_vat", "booking_engine_price_includes_other_taxes",
      "booking_engine_price_includes_fees", "booking_engine_price_includes_city_tax"].every((key) => typeof settings[key] === "boolean")
    || !nullableText(settings.booking_engine_slug) || !["hosted", "widget"].includes(settings.booking_engine_entry_mode)
    || !["pilcore-light", "editorial", "premium-dark"].includes(settings.booking_engine_theme)
    || !Array.isArray(settings.booking_engine_hero_background_screens)
    || settings.booking_engine_hero_background_screens.some((key) => !["search", "categories", "extras", "guest_details", "payment", "confirmation"].includes(key))
    || !Array.isArray(settings.booking_engine_languages) || !settings.booking_engine_languages.every(nonEmptyString)
    || !nonEmptyString(settings.booking_engine_default_language)) throw invalidSnapshot();
  const texts = settings.booking_engine_texts;
  if (texts !== null && (!onlyFields(texts, ["hero_subtitle", "hero_claim"]) || !Object.values(texts).every(translatedText))) throw invalidSnapshot();
  const emailTemplates = settings.booking_engine_guest_email_templates;
  if (!onlyFields(emailTemplates, ["en", "es"]) || Object.values(emailTemplates).some((entry) =>
    !onlyFields(entry, ["subject", "body"]) || Object.values(entry).some((text) => typeof text !== "string"))) throw invalidSnapshot();
  if (!Array.isArray(settings.booking_engine_terms)) throw invalidSnapshot();
  for (const term of settings.booking_engine_terms) {
    if (!exactFields(term, ["id", "sort_order", "title", "text"])
      || !(nonEmptyString(term.id) || Number.isSafeInteger(term.id)) || !Number.isFinite(term.sort_order)
      || !translatedText(term.title) || !translatedText(term.text)) throw invalidSnapshot();
  }
  const profile = booking.profile;
  if (profile !== null && (!exactFields(profile, BOOKING_ENGINE_PROFILE_FIELDS) || !validId(profile.id)
    || profile.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !["public_hotel_name", "address_line", "city", "postal_code", "country"].every((key) => nullableText(profile[key]))
    || !["phones", "emails"].every((key) => Array.isArray(profile[key]) && profile[key].every((text) => typeof text === "string")))) throw invalidSnapshot();
  if (!Array.isArray(booking.roomCategoryCopy) || !Array.isArray(booking.amenities) || !Array.isArray(booking.roomCategoryAmenities)) throw invalidSnapshot();
  const copyIds = new Set(); const copyCategories = new Set();
  for (const row of booking.roomCategoryCopy) {
    if (!exactFields(row, BOOKING_ENGINE_ROOM_COPY_FIELDS) || !validId(row.id) || copyIds.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || !validId(row.room_category_id) || !categories.has(row.room_category_id)
      || copyCategories.has(row.room_category_id) || !["name_en", "name_es"].every((key) => typeof row[key] === "string")
      || !["short_description_en", "short_description_es", "bed_type_en", "bed_type_es", "view_en", "view_es", "highlights_en", "highlights_es"].every((key) => nullableText(row[key]))
      || !["size_m2", "beds_count", "max_pax"].every((key) => row[key] === null || Number.isSafeInteger(row[key]))) throw invalidSnapshot();
    copyIds.add(row.id); copyCategories.add(row.room_category_id);
  }
  const amenities = new Set();
  for (const row of booking.amenities) {
    if (!exactFields(row, BOOKING_ENGINE_AMENITY_FIELDS) || !validId(row.id) || amenities.has(row.id)
      || ![null, DEMO_BUSINESS_UNIT_ID].includes(row.business_unit_id)
      || !["code", "name", "name_en", "name_es"].every((key) => typeof row[key] === "string")
      || !["category", "category_en", "category_es"].every((key) => nullableText(row[key]))
      || !Number.isSafeInteger(row.sort_order)) throw invalidSnapshot();
    amenities.add(row.id);
  }
  const links = new Set();
  for (const row of booking.roomCategoryAmenities) {
    if (!exactFields(row, ["id", "business_unit_id", "room_category_id", "amenity_id"]) || !validId(row.id) || links.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || !validId(row.room_category_id) || !categories.has(row.room_category_id)
      || !validId(row.amenity_id) || !amenities.has(row.amenity_id)) throw invalidSnapshot();
    links.add(row.id);
  }
  const assets = booking.assets;
  if (!exactFields(assets, ["heroBackground", "roomCategoryImages"]) || !Array.isArray(assets.roomCategoryImages)) throw invalidSnapshot();
  const assetIds = new Set();
  const validateAsset = (row, hero) => {
    if (!exactFields(row, BOOKING_ENGINE_ASSET_FIELDS) || !validId(row.id) || assetIds.has(row.id)
      || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID || row.is_active !== true || !Number.isSafeInteger(row.sort_order)
      || (hero ? row.kind !== "hero_background" || row.room_category_id !== null
        : !["cover", "gallery"].includes(row.kind) || !validId(row.room_category_id) || !categories.has(row.room_category_id))) throw invalidSnapshot();
    assetIds.add(row.id);
  };
  if (assets.heroBackground !== null) validateAsset(assets.heroBackground, true);
  assets.roomCategoryImages.forEach((row) => validateAsset(row, false));
  return { ...booking, roomCategories: [...categories.values()] };
}

export function validateDemoSnapshot(value) {
  if (!isObject(value) || value.version !== SNAPSHOT_VERSION
    || !isObject(value.source) || value.source.kind !== "hotel-travesia-export"
    || value.source.environment !== "production"
    || value.source.business_unit_id !== PRODUCTION_BUSINESS_UNIT_ID
    || !isObject(value.businessUnit) || value.businessUnit.id !== DEMO_BUSINESS_UNIT_ID
    || !String(value.businessUnit.name || "").trim()
    || !/^[A-Z]{3}$/.test(String(value.businessUnit.currency || ""))
    || !validDateOnly(value.reference_date)
    || typeof value.generated_at !== "string" || !Number.isFinite(Date.parse(value.generated_at))
    || !isObject(value.hotelSettings)
    || COLLECTIONS.some((key) => !Array.isArray(value[key]) || value[key].some((row) => !isObject(row)))) {
    throw invalidSnapshot();
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: value.businessUnit.timezone }).format();
  } catch (_error) {
    throw invalidSnapshot();
  }
  if (!value.businessUnit.timezone || value.reservations.some((row) =>
    !Number.isInteger(row.id) || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
    || !validDateOnly(row.check_in_date) || !validDateOnly(row.check_out_date)
    || row.check_in_date >= row.check_out_date
    || !Number.isFinite(Number(row.total_price)) || !Number.isFinite(Number(row.paid_amount))
  )) throw invalidSnapshot();
  // Optional for old v2 snapshots; once present, the series must be complete
  // and scoped. Availability Summary reports an update requirement if absent.
  if (Object.prototype.hasOwnProperty.call(value, "forecastOccupancy")) {
    if (!Array.isArray(value.forecastOccupancy)) throw invalidSnapshot();
    const reservations = new Set(value.reservations.map((row) => row.id));
    const categories = new Set(value.roomCategories.map((row) => row.id));
    const seen = new Set();
    for (const row of value.forecastOccupancy) {
      if (!isObject(row) || !Number.isSafeInteger(row.id) || row.id <= 0 || seen.has(row.id)
        || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID
        || !reservations.has(row.reservation_id) || !categories.has(row.room_category_id)
        || !validDateOnly(row.stay_date)) throw invalidSnapshot();
      seen.add(row.id);
    }
  }
  // Older v2 exports omit these columns and formula/group associations. Keep
  // other views usable; the summary endpoint requires a refreshed export.
  for (const name of ["roomRateSummaries", "roomRateDetails"]) {
    if (Object.prototype.hasOwnProperty.call(value, name)) validateRoomRateConfigurations(value, name);
  }
  for (const name of ["ratePrices", "stopSells"]) {
    if (Object.prototype.hasOwnProperty.call(value, name)) validateRateCalendar(value, name);
  }
  if (Object.prototype.hasOwnProperty.call(value, "reservationSettings")) validateDemoReservationSettings(value.reservationSettings, value.clients);
  // Legacy snapshots may contain only part of this existing catalog. Keep
  // those readable by previous screens; the Front Desk endpoint reports 412.
  if (FRONT_DESK_SETTINGS_FIELDS.every((key) => Object.prototype.hasOwnProperty.call(value.hotelSettings, key))) {
    validateDemoFrontDeskSettings(value.hotelSettings);
  }
  if (Object.prototype.hasOwnProperty.call(value, "extrasCatalog")) validateDemoExtrasSettings(value);
  if (CHARGES_COLLECTIONS.some((key) => Object.prototype.hasOwnProperty.call(value, key))) validateChargesCollections(value, false);
  if (Object.prototype.hasOwnProperty.call(value, "bookingEngineSettings")) validateDemoBookingEngineSettings(value);
  return value;
}

function freezeSnapshot(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freezeSnapshot);
    Object.freeze(value);
  }
  return value;
}

export function readDemoSnapshot() {
  if (!snapshot) throw invalidSnapshot();
  return snapshot;
}

export function peekDemoSnapshot() {
  return snapshot;
}

export function loadDemoSnapshot() {
  if (snapshot) return Promise.resolve(snapshot);
  if (snapshotPromise) return snapshotPromise;

  snapshotPromise = (async () => {
    const publicPath = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
    const url = new URL(`${publicPath}/demo-data/hotel-travesia.json`, window.location.origin);
    if (url.origin !== window.location.origin) throw invalidSnapshot();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const response = await window.fetch(url.toString(), {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        redirect: "error",
        signal: controller.signal,
      });
      if (!response.ok) throw invalidSnapshot();
      snapshot = freezeSnapshot(validateDemoSnapshot(await response.json()));
      return snapshot;
    } finally {
      window.clearTimeout(timeout);
    }
  })().catch((error) => {
    snapshotPromise = null;
    throw error;
  });
  return snapshotPromise;
}

export function demoReferenceDate() {
  return snapshot?.reference_date || "";
}

export function demoReferenceNow() {
  const date = demoReferenceDate();
  // Date-only hotel values must keep their calendar day in every visitor timezone.
  return date ? new Date(`${date}T12:00:00`) : new Date();
}
